import { useEffect, useMemo, useRef, useState } from "react";
import Select, { InputActionMeta, SingleValue } from "react-select";

import api from "../../api/api_instance";

export type SelectOption = {
  label: string;
  value: string | number;
  [key: string]: unknown;
};

type ApiRecord = Record<string, unknown>;

type SelectWithSearchProps = {
  endpoint: string;
  value: SelectOption | null;
  onChange: (option: SelectOption | null) => void;
  queryParam?: string;
  placeholder?: string;
  className?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  minChars?: number;
  labelKey?: string;
  valueKey?: string;
  additionalParams?: Record<string, string | number | boolean>;
  transformResponse?: (response: unknown) => SelectOption[];
};

const DEFAULT_DEBOUNCE_MS = 1500;

const toOptions = (
  payload: unknown,
  labelKey: string,
  valueKey: string,
): SelectOption[] => {
  const list = Array.isArray(payload)
    ? payload
    : payload &&
        typeof payload === "object" &&
        Array.isArray((payload as Record<string, unknown>).items)
      ? (payload as Record<string, unknown>).items
      : [];

  return (list as ApiRecord[])
    .map((item) => {
      const label = item[labelKey];
      const value = item[valueKey];
      if ((typeof value !== "string" && typeof value !== "number") || label == null) {
        return null;
      }

      return {
        ...item,
        label: String(label),
        value,
      };
    })
    .filter((item): item is SelectOption => item !== null);
};

export default function SelectWithSearch({
  endpoint,
  value,
  onChange,
  queryParam = "query",
  placeholder = "Search...",
  className,
  isDisabled,
  isClearable = true,
  minChars = 3,
  labelKey = "name",
  valueKey = "id",
  additionalParams,
  transformResponse,
}: SelectWithSearchProps) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const trimmedQuery = inputValue.trim();

    if (trimmedQuery.length < minChars) {
      setOptions([]);
      setIsLoading(false);
      setHasSearched(false);
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      setHasSearched(true);

      try {
        const { data } = await api.get(endpoint, {
          params: {
            ...(additionalParams ?? {}),
            [queryParam]: trimmedQuery,
          },
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        const nextOptions = transformResponse
          ? transformResponse(data)
          : toOptions(data, labelKey, valueKey);

        setOptions(nextOptions);
      } catch {
        if (requestId === requestIdRef.current) {
          setOptions([]);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, DEFAULT_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    additionalParams,
    endpoint,
    inputValue,
    labelKey,
    minChars,
    queryParam,
    transformResponse,
    valueKey,
  ]);

  const noOptionsMessage = useMemo(() => {
    if (!inputValue.trim() || inputValue.trim().length < minChars) {
      return `Type at least ${minChars} character${minChars > 1 ? "s" : ""}`;
    }
    if (isLoading) {
      return "Searching...";
    }
    if (hasSearched) {
      return "No results found";
    }
    return "Start typing to search";
  }, [hasSearched, inputValue, isLoading, minChars]);

  const handleInputChange = (newValue: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === "input-change") {
      setInputValue(newValue);
    }
  };

  return (
    <Select<SelectOption, false>
      className={className}
      value={value}
      options={options}
      isLoading={isLoading}
      isClearable={isClearable}
      isDisabled={isDisabled}
      placeholder={placeholder}
      onInputChange={handleInputChange}
      onChange={(option: SingleValue<SelectOption>) => onChange(option ?? null)}
      noOptionsMessage={() => noOptionsMessage}
      formatOptionLabel={(option) =>
        option.label.length > 0
          ? option.label.charAt(0).toUpperCase() + String(option.label).slice(1)
          : option.label
      }
      filterOption={null}
    />
  );
}
